/**
 * The launch-time trigger for an enrolment attempt this device has sent but
 * never heard back from (spec section 6.7 "Reconciling a sent attempt is a
 * recovery path, not an enrolment"; decision 40).
 *
 * WHY A TRIGGER OF ITS OWN. `enrolIfEligible` runs when the Hub opens or a
 * highlight is saved, and behind every enrolment precondition: the
 * `courses-e2ee` flag, the platform probe, the persistence gate, the memos.
 * A member whose 201 was lost may already be enrolled — the server holds a
 * bundle under a key only this phone's attempt row knows — and every one of
 * those gates can legitimately go false afterwards: a percentage rollout
 * drops them out of the bucket, they turn their passcode off, the flag is
 * pulled. The only other launch watcher (`reset-notice.js`) keys on
 * `hasMasterKey`, and an ambiguous attempt deliberately sets no key field.
 * Without this, that member would write plaintext for ever with a bundle on
 * the server refusing every write, and nothing would ever ask.
 *
 * So this watches the one signal an attempt does set — `attemptState` —
 * and, for `ambiguous`, runs the reconcile once per member per session,
 * ahead of and independently of the gates. `resumeEnrolment` joins an
 * enrolment already in flight rather than starting a second one, so the
 * watcher firing mid-attempt (the row goes `ambiguous` right before the
 * POST) costs nothing.
 *
 * A `prepared` attempt is not resumed from here: nothing was sent, so the
 * member is not enrolled anywhere, and the ordinary Hub-open path re-sends
 * it under the ordinary gates. A `rejected` one is left to the same path,
 * which reconciles it once per session.
 *
 * Never throws and never blocks: every answer is an outcome, and a rejection
 * is logged and left for the next launch.
 */
import { ATTEMPT_STATE } from '@/utils/courses-crypto/key-store';
import { resumeEnrolment } from './enrolment';

/**
 * @param {{ store: object, resume?: typeof resumeEnrolment }} args
 * @returns {() => void} stops watching
 */
export function installEnrolmentResumeCheck({ store, resume = resumeEnrolment }) {
  if (!store) throw new TypeError('installEnrolmentResumeCheck needs the root Vuex store');

  // One derived value rather than a tuple: a watcher over an array rebuilds it
  // on every evaluation and so fires on changes that are not this one.
  const currentMember = () => {
    const memberId = store.getters['courses-keys/memberId'];
    return Number.isInteger(memberId) && memberId > 0 ? memberId : 0;
  };
  const signal = () =>
    store.getters['courses-keys/attemptState'] === ATTEMPT_STATE.AMBIGUOUS ? currentMember() : 0;

  let asked = null;

  const check = member => {
    if (!member) {
      // Either the attempt settled — the latch stays, one look per member per
      // session — or the member left, and whoever comes back gets theirs.
      if (!currentMember()) asked = null;
      return;
    }
    if (asked === member) return;
    asked = member;
    resume({ store }).catch(error => {
      // An unhandled rejection here would be one in the app shell, at boot.
      // The attempt is still on disk; the next launch asks again.
      console.warn('[CoursesE2EE] A sent enrolment attempt was not reconciled at launch:', error);
    });
  };

  check(signal());
  return store.watch(signal, check);
}
